// src/lib/resumoFx.js — fundo animado do resumo (faixa "Resumo do período").
// Feixe de linhas regido por FUNÇÕES DE ENGENHARIA. Botão f() alterna a função
// (persistido em localStorage). Setups por função definidos pelo time.
// Performance: pausa fora de vista (IntersectionObserver), aba oculta e reduced-motion.

const FNS = ['damp', 'cat', 'modal', 'beat', 'euler', 'beam', 'chirp', 'gauss'];
const NM = { damp:'Vibração amortecida', cat:'Catenária', modal:'Onda modal', beat:'Batimento',
  euler:'Flambagem de Euler', beam:'Elástica de viga', chirp:'Clotoide / chirp', gauss:'Gaussiana' };
// setups calibrados (preview f())
const CFG = {
  damp:  { N:9,  def:0.27, p1:1,    p2:0,    amp:0.81, bloom:0.35 },
  cat:   { N:18, def:0.25, p1:1,    p2:0,    amp:1,    bloom:0.35 },
  modal: { N:26, def:0.59, p1:1,    p2:0,    amp:0.71, bloom:0.35 },
  beat:  { N:11, def:0.47, p1:0.45, p2:0.53, amp:1,    bloom:0.15 },
  euler: { N:24, def:0.44, p1:0.57, p2:1,    amp:0.77, bloom:0.03 },
  beam:  { N:18, def:0.34, p1:0.67, p2:0.62, amp:1,    bloom:0.39 },
  chirp: { N:12, def:0.41, p1:0.56, p2:0.7,  amp:0.6,  bloom:0.2  },
  gauss: { N:25, def:0.35, p1:0.57, p2:0.07, amp:1,    bloom:0.39 },
};
const DEFAULT = 'damp', LS = 'resumo-fn';
const REDUCE = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
const chosen = () => { try { const f = localStorage.getItem(LS); return CFG[f] ? f : DEFAULT; } catch (e) { return DEFAULT; } };
const setChosen = (f) => { try { localStorage.setItem(LS, f); } catch (e) {} };

function yfun(fn, u, k, t, n, c, H) {
  const kk = n > 1 ? k / (n - 1) : 0.5, ko = kk * 2 - 1, c0 = H * 0.5, A = H * 0.34 * c.amp;
  switch (fn) {
    case 'damp':  { const w = 3 + 18 * c.p1, d = 0.5 + 5 * c.p2; return c0 + A * Math.exp(-d * u) * Math.sin(w * u + t + ko * 0.9); }
    case 'cat':   { const a = 0.6 + 4 * c.p1, cc = Math.cosh((u - 0.5) * a) / Math.cosh(a / 2) - 1; return c0 + A * 1.4 * cc * (0.35 + 0.65 * kk) + H * 0.02 * Math.sin(t + ko); }
    case 'modal': { const w = 1 + 5 * c.p1; return c0 + A * (Math.sin(Math.PI * u * w) * Math.sin(t + ko * 1.4) + 0.45 * Math.sin(2 * Math.PI * u * w + 0.5) * Math.sin(1.3 * t)); }
    case 'beat':  return c0 + A * Math.cos(Math.PI * (0.4 + 2.2 * c.p2) * u) * Math.sin((4 + 16 * c.p1) * u + t + ko * 0.6);
    case 'euler': { const nn = 1 + 4 * c.p1; return c0 + A * Math.sin(nn * Math.PI * u) * Math.cos(ko * 1.2 + 0.2 * t); }
    case 'beam':  { const se = u - 2 * u * u * u + u * u * u * u; return c0 + A * 2.4 * se * (0.25 + 0.75 * kk) * (0.7 + 0.3 * Math.sin(t + ko)); }
    case 'chirp': return c0 + A * Math.sin((2 + 34 * c.p1) * u * u + t + ko * 0.8);
    case 'gauss': { const g = Math.exp(-Math.pow((u - 0.5) / (0.08 + 0.3 * c.p2), 2)); return c0 + A * 1.6 * g * (0.25 + 0.75 * kk) * (0.75 + 0.25 * Math.sin(t + ko)); }
  }
  return c0;
}

const instances = [];
function mount(el) {
  if (el.__fx) return; el.__fx = true; el.classList.add('has-resumo-fx');
  const cv = document.createElement('canvas'); cv.className = 'resumo-fx'; el.insertBefore(cv, el.firstChild);
  const scrim = document.createElement('div'); scrim.className = 'resumo-fx-scrim'; el.insertBefore(scrim, cv.nextSibling);
  const tip = document.createElement('div'); tip.className = 'resumo-fx-tip'; el.appendChild(tip);
  const btn = document.createElement('button'); btn.className = 'resumo-fx-btn'; btn.type = 'button'; btn.textContent = 'f()'; btn.title = 'Trocar gráfico'; el.appendChild(btn);
  const ctx = cv.getContext('2d'); const DPR = Math.min(window.devicePixelRatio || 1, 2);
  let W = 0, H = 0, grad = null, raf = null, fn = chosen();
  function resize() {
    const r = el.getBoundingClientRect(); if (!r.width) return;
    W = cv.width = Math.round(r.width * DPR); H = cv.height = Math.round(r.height * DPR);
    grad = ctx.createLinearGradient(0, 0, W, 0);
    grad.addColorStop(0, 'color-mix(in oklab, var(--sc-warn) 100%, transparent)'); grad.addColorStop(.34, 'color-mix(in oklab, var(--sc-bad) 100%, transparent)');
    grad.addColorStop(.56, 'color-mix(in oklab, var(--sc-info) 100%, transparent)'); grad.addColorStop(.8, 'color-mix(in oklab, var(--sc-info) 100%, transparent)'); grad.addColorStop(1, 'color-mix(in oklab, var(--sc-info) 100%, transparent)');
    try { draw(performance.now()); } catch (e) {}
  }
  function draw(ts) {
    const c = CFG[fn], t = ts * 0.0009, n = Math.round(c.N);
    ctx.globalCompositeOperation = 'source-over'; ctx.clearRect(0, 0, W, H);
    if (c.bloom > 0) {
      ctx.globalCompositeOperation = 'lighter'; const fx = W * 0.6, fy = H * 0.5;
      const bl = ctx.createRadialGradient(fx, fy, 0, fx, fy, H * 0.9);
      bl.addColorStop(0, 'rgba(236,72,153,' + (0.28 * c.bloom).toFixed(3) + ')');
      bl.addColorStop(.5, 'rgba(168,85,247,' + (0.12 * c.bloom).toFixed(3) + ')');
      bl.addColorStop(1, 'rgba(0,0,0,0)'); ctx.fillStyle = bl; ctx.fillRect(0, 0, W, H);
    }
    ctx.globalCompositeOperation = 'lighter'; ctx.strokeStyle = grad; ctx.lineWidth = (0.8 + 1.7 * c.def) * DPR; ctx.lineCap = 'round';
    const a = Math.min(1, 0.18 + 0.55 * c.def);
    for (let k = 0; k < n; k++) {
      ctx.globalAlpha = a; ctx.beginPath();
      for (let x = 0; x <= W; x += 5 * DPR) { const u = x / W, y = yfun(fn, u, k, t, n, c, H); x === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y); }
      ctx.stroke();
    }
    ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'destination-out';
    const fg = ctx.createLinearGradient(0, 0, W * 0.46, 0); fg.addColorStop(0, 'rgba(0,0,0,1)'); fg.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = fg; ctx.fillRect(0, 0, W * 0.46, H);
  }
  function loop(ts) { if (!cv.isConnected) { stop(); return; } draw(ts); raf = requestAnimationFrame(loop); }
  function start() { if (REDUCE) { if (W) draw(performance.now()); return; } if (!raf && !document.hidden) raf = requestAnimationFrame(loop); }
  function stop() { if (raf) { cancelAnimationFrame(raf); raf = null; } }
  btn.addEventListener('click', (e) => {
    e.stopPropagation(); fn = FNS[(FNS.indexOf(fn) + 1) % FNS.length]; setChosen(fn);
    tip.textContent = NM[fn]; tip.classList.add('show'); clearTimeout(btn._t); btn._t = setTimeout(() => tip.classList.remove('show'), 1500);
    if (REDUCE) draw(performance.now());
  });
  if ('IntersectionObserver' in window) { new IntersectionObserver((es) => { es[0].isIntersecting ? start() : stop(); }, { threshold: 0 }).observe(el); }
  if ('ResizeObserver' in window) { new ResizeObserver(resize).observe(el); } else { window.addEventListener('resize', resize); }
  resize(); start(); instances.push({ start, stop });
}
function scan() { document.querySelectorAll('.mg-resumo, .bi-banner').forEach(mount); }
let started = false, pending = false;
export function initResumoFx() {
  if (!started) {
    started = true;
    document.addEventListener('visibilitychange', () => { instances.forEach((i) => (document.hidden ? i.stop() : i.start())); });
    try {
      const mo = new MutationObserver(() => { if (pending) return; pending = true; requestAnimationFrame(() => { pending = false; scan(); }); });
      mo.observe(document.body, { childList: true, subtree: true });
    } catch (e) {}
  }
  scan();
}
